<template>
  <div class="bp-date-picker" :class="`bp-date-picker--${size}`">
    <div class="bp-date-picker__container position-relative">
      <button class="date-picker-input" @click.stop="toggle">
        <div>
          <span class="fs-14" v-if="!selectedDates">{{ label }}</span>
          <span class="fs-14 text-dark" v-else>{{ selectedDates }}</span>
        </div>
        <i class="ph ph-calendar-blank" />
      </button>
    </div>

    <responsive-modal title="Período" v-model="modal" v-if="isMobile">
      <snow-date-picker-calendar
        :isHours="isHours"
        v-model="innerValue"
        :single="single"
        @input="apply"
      />
    </responsive-modal>

    <date-picker-popper trigger="clickToToggle" :options="popperOptions" v-else>
      <div ref="popper" slot="reference" />

      <div slot="default" class="bp-date-picker__popper">
        <snow-date-picker-calendar
          :isHours="isHours"
          v-model="innerValue"
          :single="single"
          ref="calendar"
          @input="apply"
        />
      </div>
    </date-picker-popper>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Popper from "vue-popperjs";
import "vue-popperjs/dist/vue-popper.css";
import SnowDatePickerCalendar from "@/components/snow-ui/SnowDatePickerCalendar.vue";

export default {
  components: {
    SnowDatePickerCalendar,
    "date-picker-popper": Popper
  },
  props: {
    value: {
      type: [Array, String],
      default: () => ["", ""]
    },
    size: {
      type: String,
      default: "md",
      validate: value => ["sm", "md", "lg"].includes(value)
    },
    label: {
      type: String,
      default: "Selecionar data"
    },
    required: {
      type: Boolean,
      default: false
    },
    isHours: {
      type: Boolean,
      default: false
    },
    format: {
      type: String,
      default: "YYYY-MM-DD"
    },
    single: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: this.value,
      modal: false,
      isMobile: window.innerWidth < 992,
      popperOptions: {
        placement: "bottom",
        modifiers: {
          offset: {
            offset: "0,10px"
          }
        }
      }
    };
  },
  computed: {
    selectedDates() {
      if (
        !this.innerValue ||
        (Array.isArray(this.innerValue) &&
          (!this.innerValue[0] || !this.innerValue[1]))
      ) {
        return "";
      }
      if (this.isHours) {
        const date = moment(this.innerValue, [
          "YYYY-MM-DD HH:mm",
          "YYYY-MM-DDTHH:mm:ssZ",
          moment.ISO_8601
        ]);
        return date.isValid()
          ? date.format("DD/MM/YYYY HH:mm")
          : this.innerValue;
      }
      if (Array.isArray(this.innerValue)) {
        if (moment(this.innerValue[0]).isSame(this.innerValue[1], "day")) {
          return this.formatDate(this.innerValue[0]);
        }
        return `${this.formatDate(this.innerValue[0])} - ${this.formatDate(
          this.innerValue[1]
        )}`;
      }

      return this.formatDate(this.innerValue);
    }
  },
  methods: {
    toggle() {
      if (this.isMobile) {
        this.modal = !this.modal;
      } else {
        this.$refs.calendar.setStartAndEnd();
        this.$refs.popper.click();
      }
    },
    apply(v) {
      if (typeof v === "string") {
        const formatted = moment(v, this.format, true).isValid()
          ? moment(v, this.format).format(this.format)
          : v;

        this.$emit("input", formatted);
        this.toggle();
        return;
      }

      if (Array.isArray(v) && v[0] && v[1]) {
        v = v.map(d => moment(d, this.format).format(this.format));
      }
      const emittedValue = this.isHours ? v[0] : v;
      this.$emit("input", emittedValue);
      this.toggle();
    },
    formatDate(date) {
      return _.startCase(moment(date).format("DD MMM YY"));
    }
  },
  watch: {
    value: {
      handler(v) {
        if (this.isHours) {
          const formats = [
            "DD/MM/YYYY HH:mm",
            moment.ISO_8601,
            "YYYY-MM-DD HH:mm",
            "YYYY-MM-DDTHH:mm:ssZ"
          ];
          const parsed = moment(v, formats, true);
          if (parsed.isValid()) {
            this.innerValue = parsed.format(this.format);
            return;
          }
        }
        this.innerValue = v;
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.bp-date-picker {
  width: 230px;
  position: relative;

  @media screen and (max-width: 991px) {
    width: 100%;
  }
  .bp-date-picker__container {
    display: grid;
    border-radius: 4px;
    transition: all 0.2s ease-in-out !important;

    .date-picker-input {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      white-space: nowrap;
      padding: 4px 8px;
      border-radius: 8px;
      background: var(--bg-1);
      border: 0.5px solid var(--dark-10);
      font-size: 14px;
      color: var(--dark-40);
      transition: 0.2s border-color;
      &:hover {
        border-color: var(--dark-40);
      }
    }
    &:focus-within {
      button {
        border-color: var(--dark-40);
        box-shadow: 0px 0px 0px 4px rgba(0, 0, 0, 0.05);
      }
    }
  }
  .bp-date-picker__popper {
    width: 360px;
    z-index: 1000;
    border-radius: 16px;
    border: 1px solid var(--dark-10);
    background: var(--bg-1);
    transform: none !important;
    top: 34px !important;
    left: auto !important;
    right: 0px !important;

    @media screen and (max-width: 991px) {
      width: 100% !important;
    }
  }
  &--lg {
    width: 100%;
    .bp-date-picker__container {
      .date-picker-input {
        padding: 16px 20px;
        border-radius: 16px;
        font-size: 16px;
      }
    }
    .bp-date-picker__popper {
      top: 64px !important;
    }
  }
}
</style>
